import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Grid, TextField, Typography, Accordion, AccordionSummary, AccordionDetails, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import clsx from "clsx";
import { useIntl } from "react-intl";
import { PendingButton } from "../Button";
import { messages } from "./messages";
const useStyles = makeStyles(theme => ({
    heading: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5)
    },
    label: {},
    value: {},
    mismatch: {
        color: theme.palette.error.main,
        fontWeight: 600
    },
    fieldBlock: {
        marginBottom: theme.spacing(2)
    },
    inputWrapper: {
        flex: "1 1 auto",
        minWidth: 0,
        maxWidth: 200
    },
    textField: {
        margin: 0
    },
    input: {},
    searchButton: {
        flexShrink: 0
    },
    button: {},
    versionHistory: {
        fontStyle: "italic",
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5)
    },
    fieldError: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(0.5)
    },
    accordion: {},
    accordionHeading: {},
    checkDate: {
        marginLeft: "auto",
        marginRight: theme.spacing(2),
        color: theme.palette.text.secondary
    },
    checkContent: {
        width: "100%"
    }
}));
const ObjectDisplayField = ({ label, isMismatch, isVisible, value, }) => {
    const classes = useStyles();
    const intl = useIntl();
    const formatValue = (value) => typeof value === "boolean"
        ? intl.formatMessage(value ? messages.yes : messages.no)
        : value;
    return isVisible ? (_jsxs(Grid, { item: true, container: true, xs: 12, children: [_jsx(Grid, { item: true, xs: 6, children: _jsx(Typography, { className: classes.label, children: intl.formatMessage(label) }) }), _jsx(Grid, { item: true, xs: 6, children: _jsx(Typography, { component: "span", className: clsx(classes.value, {
                        [classes.mismatch]: isMismatch
                    }), children: formatValue(value) }) })] })) : null;
};
export const ObjectDisplay = ({ title, object, mismatch, isVisible }) => {
    const classes = useStyles();
    const mismatchSet = new Set(mismatch ?? []);
    return (_jsxs(_Fragment, { children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(Typography, { className: classes.heading, children: title }) }), _jsx(ObjectDisplayField, { label: messages.rowRegNo, isMismatch: mismatchSet.has("carRegNo"), isVisible: isVisible("regNo") || (object?.regNo?.length ?? 0) > 0, value: object?.regNo }), _jsx(ObjectDisplayField, { label: messages.rowChassisNo, isMismatch: mismatchSet.has("carChassisNo"), isVisible: isVisible("chassisNo") || (object?.chassisNo?.length ?? 0) > 0, value: object?.chassisNo }), _jsx(ObjectDisplayField, { label: messages.rowObjectType, isMismatch: mismatchSet.has("objectType"), isVisible: true, value: object?.objectType }), _jsx(ObjectDisplayField, { label: messages.rowCarType, isMismatch: mismatchSet.has("carType"), isVisible: true, value: object?.carType }), _jsx(ObjectDisplayField, { label: messages.rowMake, isMismatch: mismatchSet.has("make"), isVisible: true, value: object?.make }), _jsx(ObjectDisplayField, { label: messages.rowModel, isMismatch: mismatchSet.has("model"), isVisible: true, value: object?.model }), _jsx(ObjectDisplayField, { label: messages.rowEngine, isMismatch: mismatchSet.has("engine"), isVisible: true, value: object?.engine }), _jsx(ObjectDisplayField, { label: messages.rowYear, isMismatch: mismatchSet.has("year"), isVisible: true, value: object?.year }), _jsx(ObjectDisplayField, { label: messages.rowGreenCode, isMismatch: mismatchSet.has("greenCode"), isVisible: isVisible("greenCode"), value: object?.greenCode })] }));
};
export const FieldWithVersion = ({ id, title, placeholder, versionHistory, value, onChange, onSearch, disabled, type, options, error, errorText }) => {
    const classes = useStyles();
    const intl = useIntl();
    const searchDisabled = disabled || value.trim().length === 0;
    const handleKeyDown = (e) => {
        if (onSearch && e.key === "Enter") {
            e.preventDefault();
            if (!searchDisabled) {
                void onSearch();
            }
        }
    };
    return (_jsxs(Grid, { item: true, xs: 12, container: true, className: classes.fieldBlock, children: [_jsx(Grid, { item: true, xs: 12, children: _jsx(Typography, { className: classes.heading, children: title }) }), _jsxs(Grid, { item: true, xs: 12, container: true, direction: "row", spacing: 1, alignItems: "center", wrap: "nowrap", children: [_jsx(Grid, { item: true, className: classes.inputWrapper, children: _jsx(TextField, { name: id, variant: "outlined", size: "small", fullWidth: true, type: options ? undefined : type, placeholder: placeholder, select: Boolean(options), SelectProps: options ? { native: true } : undefined, className: classes.textField, InputProps: { className: classes.input }, error: error, value: value, disabled: disabled, onKeyDown: handleKeyDown, onChange: (e) => onChange(e.target.value), children: options && [
                                _jsx("option", { value: "" }, ""),
                                ...options.map(option => (_jsx("option", { value: option.value, children: option.text }, option.value)))
                            ] }) }), onSearch && (_jsx(Grid, { item: true, className: classes.searchButton, children: _jsx(PendingButton, { variant: "contained", color: "secondary", className: classes.button, onClick: onSearch, disabled: searchDisabled, children: intl.formatMessage(messages.search) }) }))] }), error && errorText && (_jsx(Grid, { item: true, xs: 12, children: _jsx(Typography, { className: classes.fieldError, children: errorText }) })), versionHistory && (_jsx(Grid, { item: true, xs: 12, children: _jsx(Typography, { className: classes.versionHistory, children: versionHistory }) }))] }));
};
export const CheckResultAccordion = ({ title, checkTime, xml, defaultExpanded }) => {
    const classes = useStyles();
    if (!xml)
        return null;
    return (_jsxs(Accordion, { defaultExpanded: defaultExpanded, className: classes.accordion, children: [_jsxs(AccordionSummary, { expandIcon: _jsx(ExpandMoreIcon, {}), children: [_jsx(Typography, { className: classes.accordionHeading, children: title }), checkTime && (_jsx(Typography, { className: classes.checkDate, children: checkTime }))] }), _jsx(AccordionDetails, { children: _jsx("div", { className: classes.checkContent, dangerouslySetInnerHTML: { __html: xml } }) })] }));
};
export const NormalizeRegNo = (value) => value.replace(/\s+/g, "").toUpperCase();
export const IsRegNoEqual = (a, b) => NormalizeRegNo(a) === NormalizeRegNo(b);
//# sourceMappingURL=EncumberanceCheckParts.js.map